/**
 * Global error boundary. Shows a calm Dutch recovery screen with a reload
 * action. Stack traces are logged to the console only — never shown to users.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { hasError: boolean }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Diagnostics stay in the console; the UI stays calm and non-technical.
    console.error('[rust] Onverwachte fout in de interface', error, info.componentStack)
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '32px 24px',
          textAlign: 'center',
          background: 'var(--warm-gray)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: '28px',
            color: 'var(--text-primary)',
          }}
        >
          Er ging iets mis
        </h1>
        <p style={{ margin: 0, maxWidth: '34ch', color: 'var(--text-secondary)' }}>
          De app is onverwacht gestopt. Je gegevens zijn bewaard gebleven. Laad de app opnieuw om
          verder te gaan.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            minHeight: '48px',
            padding: '0 28px',
            border: 'none',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--soft-blue)',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          App opnieuw laden
        </button>
      </main>
    )
  }
}
